<template>
  <ValidationObserver ref="nightCardForm" slim>
    <VRow dense class="ma-0">
    <VCol v-if="infoLinkUrl && isInfoLinkNeeded" class="px-2 pb-2" cols="12">
      <div class="d-flex align-items-center">
        <VIcon class="secondary--text">
          mdi-help-circle-outline
        </VIcon>
        <a
          :href="infoLinkUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="body-2 secondary--text text-decoration-none ml-2"
        >
          {{ $t('nightRiderLink.inviteLinkText') }}
        </a>
      </div>
    </VCol>
    <VCol class="px-2 py-0" cols="12">
      <ValidationProvider
        v-slot="{ errors, ariaInput }"
        name="nightCardNumber"
        :rules="fieldRules.cardNumber"
        mode="aggressive"
      >
        <VTextField
          v-bind="ariaInput"
          :value="inner.cardNumber"
          data-test="night-card-number-input"
          :label="$t('nightRiderLink.fieldCardNumber')"
          @input="onCardNumberInput"
          :hide-details="!errors.length"
          :error="!!errors.length"
          :error-messages="validationErrorResolve(errors[0])"
          maxlength="20"
          counter="20"
        />
      </ValidationProvider>
    </VCol>
    <VCol class="px-2 py-0" cols="12">
      <ValidationProvider
        v-slot="{ errors, ariaInput }"
        name="registeredPhone"
        :rules="fieldRules.phone"
        mode="aggressive"
      >
        <VTextField
          v-bind="ariaInput"
          v-model="inner.phone"
          data-test="night-card-phone-input"
          class="mt-0 pt-2 pb-1"
          :disabled="useAccountPhone"
          :label="$t('nightRiderLink.fieldRegisteredPhone')"
          :hide-details="!errors.length"
          :error="!!errors.length"
          :error-messages="validationErrorResolve(errors[0])"
          maxlength="20"
          counter="20"
        />
      </ValidationProvider>
    </VCol>
    <VCol class="px-2 py-0 d-flex align-items-center" cols="12">
      <VCheckbox
        v-model="useAccountPhone"
        class="mt-0"
        color="brandingPrimary"
        data-test="night-card-same-phone-checkbox"
        hide-details
        @change="onToggleSamePhone"
      />
      <span class="body-1 ml-1 checkbox-label">{{ $t('nightRiderLink.samePhoneCheckbox') }}</span>
    </VCol>
    <VCol class="px-2 py-0" cols="12">
      <ValidationProvider
        v-slot="{ errors, ariaInput }"
        name="registeredFirstName"
        :rules="fieldRules.firstName"
        mode="aggressive"
      >
        <VTextField
          v-bind="ariaInput"
          v-model="inner.firstName"
          data-test="night-card-first-name-input"
          class="mt-0 pt-0"
          :label="$t('nightRiderLink.fieldRegisteredFirstName')"
          :hide-details="!errors.length"
          :error="!!errors.length"
          :error-messages="validationErrorResolve(errors[0])"
          maxlength="50"
          counter="50"
        />
      </ValidationProvider>
    </VCol>
    <VCol class="px-2 py-0" cols="12">
      <ValidationProvider
        v-slot="{ errors, ariaInput }"
        name="registeredLastName"
        :rules="fieldRules.lastName"
        mode="aggressive"
      >
        <VTextField
          v-bind="ariaInput"
          v-model="inner.lastName"
          data-test="night-card-last-name-input"
          class="mt-0 pt-0"
          :label="$t('nightRiderLink.fieldRegisteredLastName')"
          :hide-details="!errors.length"
          :error="!!errors.length"
          :error-messages="validationErrorResolve(errors[0])"
          maxlength="50"
          counter="50"
        />
      </ValidationProvider>
    </VCol>
    <VCol v-if="showPrimaryAction" class="px-2 pt-2" cols="12">
      <VBtn
        class="w-100 text-decoration-none"
        color="brandingPrimary"
        dark
        data-test="night-card-form-submit"
        @click="onSubmit"
      >
        <span class="button">{{ primaryActionLabelResolved }}</span>
      </VBtn>
    </VCol>
    </VRow>
  </ValidationObserver>
</template>

<script lang="ts">
export default {
  name: 'SNightRiderCardLinkForm',
};
</script>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n, useValidationError } from '../../composables';

const props = defineProps({
  accountPhone: {
    type: String,
    default: '',
  },
  infoLinkUrl: {
    type: String,
    default: '',
  },
  isInfoLinkNeeded: {
    type: Boolean,
    default: false,
  },
  showPrimaryAction: {
    type: Boolean,
    default: true,
  },
  primaryActionLabel: {
    type: String,
    default: '',
  },
  /** When false, all fields optional (e.g. unused) */
  requireAllFields: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['submit']);

const { validationErrorResolve } = useValidationError();
const { t } = useI18n();

const useAccountPhone = ref(true);
const inner = ref({
  cardNumber: '',
  phone: '',
  firstName: '',
  lastName: '',
});
const nightCardForm = ref<any>(null);

const primaryActionLabelResolved = computed(() => (
  props.primaryActionLabel || t('nightRiderLink.saveCard')
));

const fieldRules = computed(() => {
  if (!props.requireAllFields) {
    return {
      cardNumber: {},
      phone: {},
      firstName: {},
      lastName: {},
    };
  }
  return {
    cardNumber: { required: true, max: 20 },
    phone: { required: true, max: 20 },
    firstName: { required: true, max: 50 },
    lastName: { required: true, max: 50 },
  };
});

function applyAccountPhone() {
  if (useAccountPhone.value) {
    inner.value.phone = props.accountPhone || '';
  }
}

watch(
  () => props.accountPhone,
  () => {
    applyAccountPhone();
  },
  { immediate: true },
);

function onToggleSamePhone() {
  if (useAccountPhone.value) {
    inner.value.phone = props.accountPhone || '';
  }
}

function onCardNumberInput(value: any) {
  inner.value.cardNumber = String(value || '').toUpperCase();
}

function reset() {
  inner.value = { cardNumber: '', phone: '', firstName: '', lastName: '' };
  useAccountPhone.value = true;
  applyAccountPhone();
  nextTick(() => nightCardForm.value?.reset());
}

async function validate() {
  return nightCardForm.value?.validate();
}

function buildSubmitPayload() {
  const phone = useAccountPhone.value ? (props.accountPhone || '') : inner.value.phone;
  const firstName = (inner.value.firstName || '').trim();
  const lastName = (inner.value.lastName || '').trim();
  return {
    cardNumber: (inner.value.cardNumber || '').trim().toUpperCase(),
    phone: (phone || '').trim(),
    firstName,
    lastName,
  };
}

async function onSubmit() {
  const ok = await validate();
  if (!ok) {
    return;
  }
  emit('submit', buildSubmitPayload());
}

/** Same as clicking Save: validate then emit `submit` (for parent-controlled buttons). */
function submitFromParent() {
  return onSubmit();
}

function getPayload() {
  return buildSubmitPayload();
}

defineExpose({
  submitFromParent,
  reset,
  validate,
  getPayload,
});
</script>

<style lang="scss" scoped>
.checkbox-label {
  padding-bottom: 21px;
}
</style>